import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAds, AdsGroupedByLocation } from '@/Ads/hooks/use-ads';
import { AdViewerSheet } from '@/Ads/components/AdViewerSheet';
import { LocationAd } from '@/Locations/types/locations';
import { useSpotlights } from '@/Spotlights/hooks/use-spotlights';
import { useUserLocation } from '@/Locations/hooks/use-user-location';
import { SpotlightCard } from '@/Spotlights/components/SpotlightCard';
import { CouponsSection } from '@/components/CouponsSection';
import { useSiteInfo } from '@/SiteInfo/hooks/use-site-info';
import { format, parseISO, isAfter, isBefore } from 'date-fns';
import { SEO } from '@/components/SEO';
import { IconAlertCircle, IconCalendar, IconCheck, IconFileText, IconMapPin } from '@tabler/icons-react';

const formatAdDate = (dateString: string): string => {
  try {
    return format(parseISO(dateString), 'MMM d');
  } catch {
    return dateString;
  }
};

const getAdStatus = (startDate: string, endDate: string): 'upcoming' | 'active' | 'expired' => {
  try {
    const now = new Date();
    const start = parseISO(startDate);
    const end = parseISO(endDate);

    if (isBefore(now, start)) return 'upcoming';
    if (isAfter(now, end)) return 'expired';
    return 'active';
  } catch {
    return 'active';
  }
};

const AdCard = ({
  ad,
  locationCity,
  locationState,
  locationBanner,
  isPreferredLocation,
  onViewAd,
}: {
  ad: LocationAd;
  locationCity: string;
  locationState: string;
  locationBanner: string;
  isPreferredLocation: boolean;
  onViewAd: () => void;
}) => {
  const status = getAdStatus(ad.AdStartDate, ad.AdEndDate);
  const dateRange = `${formatAdDate(ad.AdStartDate)} - ${formatAdDate(ad.AdEndDate)}`;

  return (
    <Card className={`hover:shadow-lg transition-shadow ${isPreferredLocation ? 'ring-2 ring-primary' : ''}`}>
      <CardHeader>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge
              variant={status === 'active' ? 'default' : status === 'upcoming' ? 'secondary' : 'destructive'}
              className={status === 'active' ? 'bg-emerald-400 hover:bg-emerald-500' : ''}
            >
              {status === 'active' && 'Active'}
              {status === 'upcoming' && 'Upcoming'}
              {status === 'expired' && 'Ending Soon'}
            </Badge>
            {isPreferredLocation && (
              <Badge variant="default" className="text-xs">
                <IconCheck className="h-3 w-3 mr-1" />
                Your Store
              </Badge>
            )}
          </div>
          {ad.ad_type?.AdType && (
            <Badge variant="outline" className="text-xs bg-white">
              {ad.ad_type.AdType}
            </Badge>
          )}
        </div>
        <CardTitle className="text-lg">{locationCity} {locationBanner}</CardTitle>
        <CardDescription className="flex items-center gap-1.5 mt-2">
          <IconMapPin className="h-4 w-4 shrink-0" />
          {locationCity}, {locationState}
        </CardDescription>
        <CardDescription className="flex items-center gap-1.5 mt-1">
          <IconCalendar className="h-4 w-4" />
          {dateRange}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={onViewAd} className="w-full" disabled={!ad.AdFile?.url}>
          <IconFileText className="h-4 w-4 mr-2" />
          View {ad.ad_type?.AdType || 'Ad'}
        </Button>
      </CardContent>
    </Card>
  );
};

const LocationAdsSection = ({
  group,
  isPreferredLocation,
  onViewAd,
}: {
  group: AdsGroupedByLocation;
  isPreferredLocation: boolean;
  onViewAd: (ad: LocationAd, locationTitle: string, locationBanner: string) => void;
}) => {
  return (
    <>
      {group.ads.map((ad, index) => (
        <AdCard
          key={`${group.locationTitle}-${ad.AdTitle}-${index}`}
          ad={ad}
          locationCity={group.locationCity}
          locationState={group.locationState}
          locationBanner={group.locationBanner}
          isPreferredLocation={isPreferredLocation}
          onViewAd={() => onViewAd(ad, group.locationTitle, group.locationBanner)}
        />
      ))}
    </>
  );
};

const Ads = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { adsGroupedByLocation, adsWithLocations, totalAdsCount, isLoading, error } = useAds();
  const { data: spotlights, isLoading: spotlightsLoading } = useSpotlights();
  const { data: siteInfo } = useSiteInfo();
  const { selectedLocation: preferredLocation, isSelected: isPreferredLocation } = useUserLocation();
  const [selectedAd, setSelectedAd] = useState<LocationAd | null>(null);
  const [selectedLocationTitle, setSelectedLocationTitle] = useState<string>('');
  const [selectedLocationBanner, setSelectedLocationBanner] = useState<string>('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [adTypeFilter, setAdTypeFilter] = useState<string>('all');

  const hasCoupons = siteInfo?.SiteHasCoupons ?? false;

  // Check if there are any actual spotlight details to display
  const hasSpotlightDetails = spotlights?.some(
    (s) => s.SpotlightDetails && s.SpotlightDetails.length > 0
  ) ?? false;

  // Handle URL param to open ad viewer sheet
  useEffect(() => {
    const openParam = searchParams.get('open');
    if (openParam && adsWithLocations) {
      const adWithLocation = adsWithLocations.find(a => a.ad.AdTitle === openParam);
      if (adWithLocation) {
        setSelectedAd(adWithLocation.ad);
        setSelectedLocationTitle(adWithLocation.location.title);
        setSelectedLocationBanner(adWithLocation.location.banner);
        setSheetOpen(true);
        // Clear the URL param after opening
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, adsWithLocations, setSearchParams]);

  // Get unique ad types for the filter
  const adTypes = useMemo(() => {
    const types = new Set<string>();
    adsGroupedByLocation.forEach((group) => {
      group.ads.forEach((ad) => {
        if (ad.ad_type?.AdType) {
          types.add(ad.ad_type.AdType);
        }
      });
    });
    return Array.from(types).sort();
  }, [adsGroupedByLocation]);

  // Filter ads by type
  const filteredAdsGroupedByLocation = useMemo(() => {
    if (adTypeFilter === 'all') return adsGroupedByLocation;

    return adsGroupedByLocation
      .map((group) => ({
        ...group,
        ads: group.ads.filter((ad) => ad.ad_type?.AdType === adTypeFilter),
      }))
      .filter((group) => group.ads.length > 0);
  }, [adsGroupedByLocation, adTypeFilter]);

  // Sort ads: preferred location first, then alphabetically
  const sortedAdsGroupedByLocation = useMemo(() => {
    if (!preferredLocation) return filteredAdsGroupedByLocation;

    return [...filteredAdsGroupedByLocation].sort((a, b) => {
      const aIsPreferred = isPreferredLocation(a.locationTitle);
      const bIsPreferred = isPreferredLocation(b.locationTitle);

      if (aIsPreferred && !bIsPreferred) return -1;
      if (!aIsPreferred && bIsPreferred) return 1;
      return 0;
    });
  }, [filteredAdsGroupedByLocation, preferredLocation, isPreferredLocation]);

  const handleViewAd = (ad: LocationAd, locationTitle: string, locationBanner: string) => {
    setSelectedAd(ad);
    setSelectedLocationTitle(locationTitle);
    setSelectedLocationBanner(locationBanner);
    setSheetOpen(true);
  };

  return (
    <div>
      <SEO
        title="Get the Latest Store Ads | Sales, Weekly Ads, Coupons"
        description="View our latest weekly ads, circulars, and special promotions. Save big on groceries, produce, and more."
      />
      <div className="bg-background py-12">
        <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Store Ads</h1>
          <div className="hidden sm:flex items-center gap-4">
            {adTypes.length > 1 && (
              <Select value={adTypeFilter} onValueChange={setAdTypeFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {adTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Badge variant="secondary" className="bg-red-400 hover:bg-red-500 text-white">
              {isLoading ? '...' : `${totalAdsCount} ad${totalAdsCount !== 1 ? 's' : ''} available`}
            </Badge>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <IconAlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>Failed to load ads. Please try again later.</AlertDescription>
          </Alert>
        )}

        {isLoading && (
          <div className="space-y-8">
            {[1, 2].map((i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="h-8 w-64" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((j) => (
                    <Card key={j}>
                      <CardHeader>
                        <Skeleton className="h-6 w-20 mb-2" />
                        <Skeleton className="h-6 w-48" />
                        <Skeleton className="h-4 w-32 mt-2" />
                      </CardHeader>
                      <CardContent>
                        <Skeleton className="h-10 w-full" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && !error && sortedAdsGroupedByLocation.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedAdsGroupedByLocation.map((group) => (
              <LocationAdsSection
                key={group.locationTitle}
                group={group}
                isPreferredLocation={isPreferredLocation(group.locationTitle)}
                onViewAd={handleViewAd}
              />
            ))}
          </div>
        )}

        {!isLoading && !error && sortedAdsGroupedByLocation.length === 0 && (
          <Alert>
            <IconAlertCircle className="h-4 w-4" />
            <AlertTitle>No Ads Available</AlertTitle>
            <AlertDescription>
              There are no weekly ads available at this time. Please check back later.
            </AlertDescription>
          </Alert>
        )}

        <AdViewerSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          ad={selectedAd}
          locationTitle={selectedLocationTitle}
          locationBanner={selectedLocationBanner}
        />
        </div>
      </div>

      {/* Spotlight Items Section - Full Width */}
      {spotlightsLoading && (
        <section className="py-12 bg-background border-t border-neutral-200">
          <div className="max-w-7xl mx-auto px-4">
            <Skeleton className="h-8 w-48 mb-6" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((item) => (
                <Card key={item}>
                  <Skeleton className="aspect-square" />
                  <CardContent className="p-4">
                    <Skeleton className="h-8 w-20 mb-2" />
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {!spotlightsLoading && hasSpotlightDetails && (
        <section className="py-12 bg-background border-t border-neutral-200">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-foreground mb-6">Spotlight Items</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {spotlights?.flatMap((spotlight) =>
                spotlight.SpotlightDetails.map((detail, index) => (
                  <SpotlightCard
                    key={`${spotlight.id}-${index}`}
                    spotlight={detail}
                  />
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {hasCoupons && (
        <CouponsSection
          filterType="Weekly Specials"
          limit={5}
          className="border-t border-neutral-200"
          title="Weekly Specials"
          subtitle="This week's best deals picked just for you"
        />
      )}
    </div>
  );
};

export default Ads;
